import React, { useEffect, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { Link } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useAppSelector } from "../../store";

// ChartJS bileşenlerini kaydet
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Örnek veri (gerçek uygulamada API'den alınacak)
const chatbotData = {
  totalChatbots: 12,
  activeChatbots: 8,
  totalConversations: 1245,
  averageResponseTime: "1.8s",
  conversationsTrend: [
    120, 150, 180, 220, 250, 300, 350, 400, 380, 420, 450, 480,
  ],
  satisfactionRate: 92,
};

const DashboardPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Sayfa yüklendiğinde veri yükleme simülasyonu
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Konuşma trendi grafiği için veri
  const conversationChartData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Conversations",
        data: chatbotData.conversationsTrend,
        fill: true,
        backgroundColor: "rgba(37, 99, 235, 0.1)",
        borderColor: "#2563EB",
        tension: 0.4,
      },
    ],
  };

  // Chatbot performans grafiği için veri
  const performanceChartData = {
    labels: ["Sales Bot", "Support Bot", "FAQ Bot", "Lead Gen Bot"],
    datasets: [
      {
        label: "Conversations",
        data: [350, 275, 190, 320],
        backgroundColor: "#2563EB",
      },
      {
        label: "Conversion Rate (%)",
        data: [25, 18, 12, 30],
        backgroundColor: "#9333EA",
      },
    ],
  };

  // Yükleniyor durumu
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <style>{`
        .dashboard-page { background: ${isDark ? "#0f172a" : "#f8f9fa"}; padding: 8px; }
        .dashboard-page .card { background: ${isDark ? "#111827" : "#ffffff"}; border: 1px solid ${isDark ? "#334155" : "#e9ecef"}; box-shadow: ${isDark ? "0 2px 12px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.08)"}; }
        .dashboard-page .card-header { border-color: ${isDark ? "#334155" : "#dee2e6"}; color: ${isDark ? "#e5e7eb" : "#111827"}; }
        .dashboard-page h1, .dashboard-page h5, .dashboard-page .stat-label { color: ${isDark ? "#e2e8f0" : "#111827"}; }
        .dashboard-page .text-muted, .dashboard-page .list-group-item small { color: ${isDark ? "#94a3b8 !important" : "#6c757d !important"}; }
        .dashboard-page .list-group-item { background: ${isDark ? "#0b1220" : "#ffffff"}; border-color: ${isDark ? "#1e293b" : "#e9ecef"}; }
        .stat-card { transition: transform .2s ease; }
        .stat-card:hover { transform: translateY(-2px); }
      `}</style>
      {/* Sayfa başlığı */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">Dashboard</h1>
          <p className="text-muted mb-0">
            Welcome back, {user?.full_name || "User"}!
          </p>
        </div>
        <div>
          <Link to="/reports/generate" className="btn btn-primary">
            <i className="bi bi-file-earmark-bar-graph me-2"></i>
            Generate Report
          </Link>
        </div>
      </div>

      {/* İstatistik kartları */}
      <div className="row g-4 mb-4">
        {/* Toplam Chatbot */}
        <div className="col-md-6 col-lg-3">
          <div className="card stat-card h-100 border-start border-primary border-4">
            <div className="card-body">
              <div className="stat-icon text-primary">
                <i className="bi bi-chat-dots-fill"></i>
              </div>
              <h6 className="stat-label">Total Chatbots</h6>
              <div className="stat-value text-primary">
                {chatbotData.totalChatbots}
              </div>
              <div className="stat-change positive">
                <i className="bi bi-arrow-up me-1"></i>
                <span>+2 from last month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Aktif Chatbot */}
        <div className="col-md-6 col-lg-3">
          <div className="card stat-card h-100 border-start border-success border-4">
            <div className="card-body">
              <div className="stat-icon text-success">
                <i className="bi bi-chat-text-fill"></i>
              </div>
              <h6 className="stat-label">Active Chatbots</h6>
              <div className="stat-value text-success">
                {chatbotData.activeChatbots}
              </div>
              <div className="stat-change positive">
                <i className="bi bi-arrow-up me-1"></i>
                <span>+1 from last month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toplam Konuşma */}
        <div className="col-md-6 col-lg-3">
          <div className="card stat-card h-100 border-start border-info border-4">
            <div className="card-body">
              <div className="stat-icon text-info">
                <i className="bi bi-chat-square-text-fill"></i>
              </div>
              <h6 className="stat-label">Total Conversations</h6>
              <div className="stat-value text-info">
                {chatbotData.totalConversations}
              </div>
              <div className="stat-change positive">
                <i className="bi bi-arrow-up me-1"></i>
                <span>+23% from last month</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ortalama Yanıt Süresi */}
        <div className="col-md-6 col-lg-3">
          <div className="card stat-card h-100 border-start border-warning border-4">
            <div className="card-body">
              <div className="stat-icon text-warning">
                <i className="bi bi-clock-fill"></i>
              </div>
              <h6 className="stat-label">Avg. Response Time</h6>
              <div className="stat-value text-warning">
                {chatbotData.averageResponseTime}
              </div>
              <div className="stat-change negative">
                <i className="bi bi-arrow-down me-1"></i>
                <span>-0.3s from last month</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="row g-4 mb-4">
        {/* Konuşma Trendi */}
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-header bg-transparent">
              <h5 className="card-title mb-0">Conversation Trend</h5>
            </div>
            <div className="card-body">
              <div className="chart-container">
                <Line
                  data={conversationChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Memnuniyet Oranı */}
        <div className="col-lg-4">
          <div className="card h-100">
            <div className="card-header bg-transparent">
              <h5 className="card-title mb-0">Satisfaction Rate</h5>
            </div>
            <div className="card-body d-flex flex-column justify-content-center align-items-center">
              <div
                className="position-relative"
                style={{ width: "180px", height: "180px" }}
              >
                <div className="position-absolute top-50 start-50 translate-middle text-center">
                  <h2 className="mb-0 fw-bold">
                    {chatbotData.satisfactionRate}%
                  </h2>
                  <p className="mb-0 text-muted">Satisfied</p>
                </div>
                <svg width="180" height="180" viewBox="0 0 180 180">
                  <circle
                    cx="90"
                    cy="90"
                    r="80"
                    fill="none"
                    stroke="#f1f1f1"
                    strokeWidth="12"
                  />
                  <circle
                    cx="90"
                    cy="90"
                    r="80"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="12"
                    strokeDasharray={2 * Math.PI * 80}
                    strokeDashoffset={
                      2 *
                      Math.PI *
                      80 *
                      (1 - chatbotData.satisfactionRate / 100)
                    }
                    strokeLinecap="round"
                    transform="rotate(-90 90 90)"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="bi bi-arrow-up-circle-fill text-success me-2"></i>
                  <span>+5% from last month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chatbot Performansı ve Son Aktiviteler */}
      <div className="row g-4">
        {/* Chatbot Performansı */}
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-header bg-transparent">
              <h5 className="card-title mb-0">Chatbot Performance</h5>
            </div>
            <div className="card-body">
              <div className="chart-container">
                <Bar
                  data={performanceChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Son Aktiviteler */}
        <div className="col-lg-4">
          <div className="card h-100">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Recent Activities</h5>
              <Link to="/activities" className="btn btn-sm btn-link">
                View All
              </Link>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                <div className="list-group-item border-0 py-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div className="bg-primary text-white rounded-circle p-2">
                        <i className="bi bi-chat-dots"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">New conversation started</h6>
                      <p className="mb-1 small">
                        Sales Bot had 5 new conversations
                      </p>
                      <small className="text-muted">5 minutes ago</small>
                    </div>
                  </div>
                </div>
                <div className="list-group-item border-0 py-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div className="bg-success text-white rounded-circle p-2">
                        <i className="bi bi-check-circle"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Chatbot updated</h6>
                      <p className="mb-1 small">
                        Support Bot was updated with new features
                      </p>
                      <small className="text-muted">2 hours ago</small>
                    </div>
                  </div>
                </div>
                <div className="list-group-item border-0 py-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div className="bg-warning text-white rounded-circle p-2">
                        <i className="bi bi-exclamation-circle"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Training completed</h6>
                      <p className="mb-1 small">
                        FAQ Bot training completed successfully
                      </p>
                      <small className="text-muted">1 day ago</small>
                    </div>
                  </div>
                </div>
                <div className="list-group-item border-0 py-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div className="bg-info text-white rounded-circle p-2">
                        <i className="bi bi-graph-up"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Weekly report generated</h6>
                      <p className="mb-1 small">
                        Weekly analytics report is ready
                      </p>
                      <small className="text-muted">2 days ago</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
